import { useEffect, useState, useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import {
  loadPolygonData,
  loadPolygonValidationFailures,
  getPolygonVersions,
  type PolygonDataset,
  type PolygonFailuresDataset,
} from "@/services/prPolygonService"
import type { RangeValue } from "@/components/shared/TableFiltersSelector"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import PrPolygonsTable from "./PrPolygonsTable"
import {
  parsePolygonUrlState,
  buildPolygonSearchParams,
  type PrPolygonUrlState,
} from "./prPolygonUrlState"

const PAGE_TITLE: string = "Parasite Rate Polygons"

/**
 * Top-level interface component for the PR Polygons data page.
 *
 * @returns A React element filling the available page space with the data
 *   grid, a loader, or an error message.
 */
export default function PrPolygonsInterface(): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read the shareable state out of the URL exactly once, on mount. Subsequent
  // URL writes are driven by state, so re-parsing on every render would fight
  // the single writer below.
  const initialUrlState: PrPolygonUrlState = useMemo(
    () => parsePolygonUrlState(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const [dataset, setDataset] = useState<PolygonDataset | null>(null)
  const [failuresDataset, setFailuresDataset] = useState<PolygonFailuresDataset | null>(null)
  const [versions, setVersions] = useState<string[]>([])
  const [currentVersion, setCurrentVersion] = useState<string>("")
  // When true the grid shows validation failures for the current version
  // instead of the main snapshot. Seeded from the URL so a shared failures link
  // opens straight into failure mode.
  const [failureMode, setFailureMode] = useState<boolean>(initialUrlState.failureMode ?? false)
  const [categoricalFilters, setCategoricalFilters] = useState<Map<string, Set<string>>>(
    initialUrlState.categoricalFilters,
  )
  const [yearRange, setYearRange] = useState<RangeValue | null>(initialUrlState.yearRange ?? null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    /**
     * Fetch the available versions and the latest version's data on mount.
     * Errors are surfaced to the UI rather than swallowed so a broken export or
     * missing version is visible.
     */
    async function fetchInitialData(): Promise<void> {
      try {
        setIsLoading(true)
        setError(null)

        const token: string = await getAccessToken()

        // Honour a version from a shared URL on load; otherwise loadPolygonData
        // with no version resolves to the latest supported version.
        const urlVersion: string | undefined = initialUrlState.version

        // Load the version list, the dataset, and (when the URL seeded failure
        // mode) the failures dataset together.
        const [versionsResponse, loaded, loadedFailures] = await Promise.all([
          getPolygonVersions(token),
          loadPolygonData(token, urlVersion),
          initialUrlState.failureMode === true
            ? loadPolygonValidationFailures(token, urlVersion)
            : Promise.resolve(null),
        ])

        setVersions(versionsResponse.versions)
        setDataset(loaded)
        if (loadedFailures != null) {
          setFailuresDataset(loadedFailures)
        }
        // Pin the selector to whatever version the data actually came from.
        setCurrentVersion(loaded.version)
      } catch (err) {
        const message: string = err instanceof Error ? err.message : "Failed to load PR polygon data"
        console.error("PrPolygonsInterface: data load failed:", err)
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchInitialData()
  }, [getAccessToken, initialUrlState.failureMode, initialUrlState.version])

  /**
   * Reload the dataset for a newly selected version.
   *
   * @param version - The dataset version string selected by the user.
   */
  const handleVersionChange = useCallback(async (version: string): Promise<void> => {
    try {
      setIsLoading(true)
      setError(null)

      const token: string = await getAccessToken()
      // Load the main dataset, and the failures dataset too when in failure
      // mode, concurrently.
      const [loaded, loadedFailures] = await Promise.all([
        loadPolygonData(token, version),
        failureMode ? loadPolygonValidationFailures(token, version) : Promise.resolve(null),
      ])
      setDataset(loaded)
      if (loadedFailures != null) {
        setFailuresDataset(loadedFailures)
      }
      setCurrentVersion(loaded.version)
    } catch (err) {
      const message: string = err instanceof Error ? err.message : "Failed to load PR polygon data"
      console.error("PrPolygonsInterface: version change failed:", err)
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAccessToken, failureMode])

  /**
   * Toggle between the main snapshot and the validation-failures view. Failures
   * are loaded lazily the first time the mode is turned on for a version, then
   * served from the service's cache thereafter.
   */
  const handleToggleFailureMode = useCallback(async (): Promise<void> => {
    // Turning failure mode OFF needs no fetch — just flip back to the data view.
    if (failureMode) {
      setFailureMode(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const token: string = await getAccessToken()
      const loadedFailures: PolygonFailuresDataset = await loadPolygonValidationFailures(
        token,
        currentVersion !== "" ? currentVersion : undefined,
      )
      setFailuresDataset(loadedFailures)
      setFailureMode(true)
    } catch (err) {
      const message: string = err instanceof Error ? err.message : "Failed to load polygon validation failures"
      console.error("PrPolygonsInterface: failures load failed:", err)
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAccessToken, failureMode, currentVersion])

  /**
   * Receive updated filters from the table's filter dialog and store them here
   * (lifted state). The URL writer below mirrors them into the query string.
   *
   * @param filters - The field → selected-values map from the dialog.
   * @param ranges - The range filters keyed by filter key (year here), if any.
   */
  const handleFiltersChange = useCallback(
    (filters: Map<string, Set<string>>, ranges: RangeValue | null): void => {
      setCategoricalFilters(filters)
      setYearRange(ranges)
    },
    [],
  )

  // Single writer that mirrors the shareable state (version, failure mode, and
  // the filters) into the URL query string. Uses replace (not push) so routine
  // filter tweaks do not spam browser history. Held back until the initial load
  // has resolved a concrete version, so it never writes an empty version param
  // over a shared URL before the data arrives.
  useEffect(() => {
    if (currentVersion === "") {
      return
    }
    const nextParams: URLSearchParams = buildPolygonSearchParams({
      version: currentVersion,
      failureMode,
      categoricalFilters,
      yearRange,
    })
    setSearchParams(nextParams, { replace: true })
  }, [currentVersion, failureMode, categoricalFilters, yearRange, setSearchParams])

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardHeader className="pb-4">
          <CardTitle><h1>{PAGE_TITLE}</h1></CardTitle>
          <CardDescription>
            Polygon-based parasite rate data
            {currentVersion !== "" ? <> · version <span className="font-medium">{currentVersion}</span></> : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center min-h-0">
              <LoadingSpinner loadingContent="Loading PR polygon data..." size="default" />
            </div>
          ) : error != null ? (
            <div className="flex flex-1 items-center justify-center min-h-0">
              <p className="text-destructive text-sm" role="alert">{error}</p>
            </div>
          ) : dataset != null ? (
            <PrPolygonsTable
              rows={failureMode ? (failuresDataset?.rows ?? []) : dataset.rows}
              versions={versions}
              currentVersion={currentVersion}
              onVersionChange={handleVersionChange}
              failureMode={failureMode}
              onToggleFailureMode={handleToggleFailureMode}
              categoricalFilters={categoricalFilters}
              yearRange={yearRange}
              onFiltersChange={handleFiltersChange}
            />
          ) : null}
        </CardContent>
      </Card>
    </>
  )
}
